import {useEffect, useState} from 'react';
import {computePeaks, PEAK_RESOLUTION} from './waveformPeaks';
import {logWaveformDecodeFailed} from '../insightsLog';

export const WAVEFORM_MAX_SECONDS = 30 * 60;
const DECODE_SAMPLE_RATE = 8000;

const cache = new Map<string, number[]>();

type OfflineContextCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

const offlineContext = (): OfflineAudioContext | null => {
    const Ctor: OfflineContextCtor | undefined = window.OfflineAudioContext
        ?? (window as unknown as {webkitOfflineAudioContext?: OfflineContextCtor}).webkitOfflineAudioContext;
    return Ctor ? new Ctor(1, 1, DECODE_SAMPLE_RATE) : null;
};

const decodePeaks = async (url: string): Promise<number[]> => {
    const context = offlineContext();
    if (!context) throw new Error('Web Audio is unavailable');
    const response = await fetch(url);
    const buffer = await context.decodeAudioData(await response.arrayBuffer());
    const channels = Array.from({length: buffer.numberOfChannels}, (_, index) => buffer.getChannelData(index));
    return computePeaks(channels, PEAK_RESOLUTION);
};

export const useWaveformPeaks = (url: string | null, durationSeconds: number): number[] | null => {
    const tooLong = durationSeconds > WAVEFORM_MAX_SECONDS;
    const [state, setState] = useState<{url: string | null; peaks: number[] | null}>(() => ({
        url,
        peaks: url ? cache.get(url) ?? null : null,
    }));

    useEffect(() => {
        if (!url || tooLong) return;
        const cached = cache.get(url);
        if (cached) {
            setState({url, peaks: cached});
            return;
        }
        let cancelled = false;
        decodePeaks(url)
            .then((peaks) => {
                cache.set(url, peaks);
                if (!cancelled) setState({url, peaks});
            })
            .catch(() => {
                if (!cancelled) logWaveformDecodeFailed();
            });
        return () => {
            cancelled = true;
        };
    }, [url, tooLong]);

    return state.url === url && !tooLong ? state.peaks : null;
};
